import { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, CheckCircle2, CodeXml, Download, LoaderCircle, LockKeyhole, X } from 'lucide-react'
import type { ToolDefinition } from '../types'
import { Logo } from './Logo'
import { WorkbenchIcon } from './WorkbenchIcon'

interface HtmlWorkspaceProps {
  tool: ToolDefinition
  onClose: () => void
}

type OutputFormat = 'png' | 'jpeg' | 'svg'

const starterHtml = `<main class="work-ticket">
  <span>zozinimg / workbench</span>
  <h1>HTML,<br>한 장으로 끝.</h1>
  <p>코드는 브라우저 안에서만 렌더링한다.</p>
  <strong>SERVER UPLOAD 0 BYTE</strong>
</main>`

const starterCss = `* { box-sizing: border-box; }
body {
  margin: 0;
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 56px;
  background: #0a0a0a;
  color: #f7f6f0;
  font-family: "Nanum Gothic", sans-serif;
}
.work-ticket {
  width: 100%;
  padding: 72px 68px;
  border: 3px solid #f7f6f0;
  border-radius: 2px;
  background: repeating-linear-gradient(135deg, #111 0 20px, #151515 20px 21px);
  box-shadow: 18px 18px 0 #ffd83d;
}
.work-ticket > span, strong { color: #ffd83d; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
h1 { max-width: 760px; margin: 28px 0; font-size: 76px; line-height: 1.02; letter-spacing: -.06em; }
p { max-width: 620px; margin-bottom: 52px; color: #b5b2a9; font-size: 22px; line-height: 1.7; }`

function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = name
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}

function sanitizeMarkup(markup: string) {
  const documentNode = new DOMParser().parseFromString(markup, 'text/html')
  documentNode.querySelectorAll('script, iframe, object, embed, link, meta, base').forEach((node) => node.remove())
  documentNode.querySelectorAll('*').forEach((node) => {
    Array.from(node.attributes).forEach((attribute) => {
      const value = attribute.value.trim().toLowerCase()
      if (attribute.name.toLowerCase().startsWith('on')) node.removeAttribute(attribute.name)
      if ((attribute.name === 'src' || attribute.name === 'href') && !value.startsWith('data:') && !value.startsWith('#')) {
        node.removeAttribute(attribute.name)
      }
    })
  })
  return new XMLSerializer().serializeToString(documentNode.body)
}

function svgSource(markup: string, css: string, width: number, height: number, background: string) {
  const safeCss = css.replace(/<\/style/gi, '<\\/style').replace(/url\s*\([^)]*\)/gi, 'none')
  const body = sanitizeMarkup(markup)
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
  <rect width="100%" height="100%" fill="${background}" />
  <foreignObject x="0" y="0" width="100%" height="100%">
    <div xmlns="http://www.w3.org/1999/xhtml" style="width:100%;height:100%;overflow:hidden;background:${background}">
      <style>${safeCss}</style>
      ${body}
    </div>
  </foreignObject>
</svg>`
}

function svgToRaster(source: string, width: number, height: number, format: Exclude<OutputFormat, 'svg'>, background: string) {
  return new Promise<Blob>((resolve, reject) => {
    const image = new Image()
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const context = canvas.getContext('2d')
        if (!context) {
          reject(new Error('이 브라우저에서는 이미지 캔버스를 만들 수 없다.'))
          return
        }
        context.fillStyle = background
        context.fillRect(0, 0, width, height)
        context.drawImage(image, 0, 0, width, height)
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error('결과 이미지를 만들지 못했다.'))),
          format === 'jpeg' ? 'image/jpeg' : 'image/png',
          0.92,
        )
      } catch {
        reject(new Error('브라우저 보안 정책이 막았다. 외부 리소스를 지우고 다시 돌려줘.'))
      }
    }
    image.onerror = () => {
      reject(new Error('HTML을 렌더링하지 못했다. 마크업을 확인해줘.'))
    }
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`
  })
}

export function HtmlWorkspace({ tool, onClose }: HtmlWorkspaceProps) {
  const [html, setHtml] = useState(starterHtml)
  const [css, setCss] = useState(starterCss)
  const [width, setWidth] = useState(1200)
  const [height, setHeight] = useState(800)
  const [background, setBackground] = useState('#0a0a0a')
  const [format, setFormat] = useState<OutputFormat>('png')
  const [processing, setProcessing] = useState(false)
  const [result, setResult] = useState<{ blob: Blob; url: string; name: string } | null>(null)
  const [message, setMessage] = useState('')

  const previewWidth = Math.max(320, Math.min(4000, Number.isFinite(width) ? width : 1200))
  const previewHeight = Math.max(240, Math.min(4000, Number.isFinite(height) ? height : 800))

  const previewImage = useMemo(
    () => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgSource(html, css, previewWidth, previewHeight, background))}`,
    [background, css, html, previewHeight, previewWidth],
  )

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !processing) onClose()
    }
    window.addEventListener('keydown', closeOnEscape)
    document.body.classList.add('workspace-open')
    return () => {
      window.removeEventListener('keydown', closeOnEscape)
      document.body.classList.remove('workspace-open')
    }
  }, [onClose, processing])

  useEffect(() => () => {
    if (result) URL.revokeObjectURL(result.url)
  }, [result])

  const clearResult = () => {
    if (result) URL.revokeObjectURL(result.url)
    setResult(null)
    setMessage('')
  }

  const makeImage = async () => {
    const safeWidth = Math.max(320, Math.min(4000, Math.round(width)))
    const safeHeight = Math.max(240, Math.min(4000, Math.round(height)))
    setWidth(safeWidth)
    setHeight(safeHeight)
    clearResult()
    setProcessing(true)
    try {
      await document.fonts.ready
      const source = svgSource(html, css, safeWidth, safeHeight, background)
      const svg = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
      const blob = format === 'svg' ? svg : await svgToRaster(source, safeWidth, safeHeight, format, background)
      const url = URL.createObjectURL(blob)
      const name = `zozinimg-html-${safeWidth}x${safeHeight}.${format === 'jpeg' ? 'jpg' : format}`
      setResult({ blob, url, name })
      setMessage(`끝. ${safeWidth}×${safeHeight} 이미지로 뽑았다.`)
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'HTML 작업이 멈췄다. 코드를 확인해줘.')
    } finally {
      setProcessing(false)
    }
  }

  return (
    <div className="workspace" role="dialog" aria-modal="true" aria-label={`${tool.title} 작업 화면`}>
      <header className="workspace-header">
        <Logo compact />
        <div className="workspace-title">
          <span className={`tool-mini-icon accent-${tool.accent}`}><WorkbenchIcon id={tool.id} size={22} /></span>
          <span><small>WORKBENCH OPEN</small><strong>{tool.title}</strong></span>
        </div>
        <button type="button" className="workspace-close" onClick={onClose} disabled={processing} aria-label="작업 화면 닫기"><X size={22} /></button>
      </header>

      <main className="workspace-main html-workspace-main">
        <section className="workspace-copy">
          <button type="button" className="back-link" onClick={onClose} disabled={processing}><ArrowLeft size={16} /> 작업대로 돌아가기</button>
          <p>WORK ORDER / 01</p>
          <h1>{tool.title}</h1>
          <span>{tool.description}</span>
        </section>

        <div className="html-workspace-grid">
          <section className="html-editor-panel">
            <div className="html-panel-heading"><div><span>CODE INPUT</span><h3>코드를 올려둔다</h3></div><small>스크립트·외부 리소스 제외</small></div>
            <label className="code-field"><span>HTML</span><textarea value={html} spellCheck={false} onChange={(event) => { setHtml(event.target.value); clearResult() }} /></label>
            <label className="code-field"><span>CSS</span><textarea value={css} spellCheck={false} onChange={(event) => { setCss(event.target.value); clearResult() }} /></label>
          </section>

          <section className="html-preview-panel">
            <div className="html-panel-heading"><div><span>LIVE OUTPUT</span><h3>나올 모양을 본다</h3></div><small>{width} × {height}</small></div>
            <div className="html-preview-shell" style={{ aspectRatio: `${previewWidth} / ${previewHeight}` }}>
              <img src={result?.url || previewImage} alt={result ? '생성된 HTML 캡처' : 'HTML 미리보기'} />
            </div>
          </section>

          <aside className="options-panel html-options-panel">
            <div className="options-heading"><div><span>SETTING / 02</span><h3>어떻게 뽑을까</h3></div></div>
            <div className="option-grid">
              <label className="option-field"><span>너비</span><div className="number-input"><input type="number" min="320" max="4000" value={width} onChange={(event) => { setWidth(Number(event.target.value)); clearResult() }} /><em>px</em></div></label>
              <label className="option-field"><span>높이</span><div className="number-input"><input type="number" min="240" max="4000" value={height} onChange={(event) => { setHeight(Number(event.target.value)); clearResult() }} /><em>px</em></div></label>
            </div>
            <label className="option-field"><span>결과 형식</span><select value={format} onChange={(event) => { setFormat(event.target.value as OutputFormat); clearResult() }}><option value="png">PNG · 선명한 이미지</option><option value="jpeg">JPG · 가벼운 이미지</option><option value="svg">SVG · 벡터 컨테이너</option></select></label>
            <label className="option-field"><span>바탕색</span><input className="color-input" type="color" value={background} onChange={(event) => { setBackground(event.target.value); clearResult() }} /></label>
            <p className="option-note"><LockKeyhole size={13} /> 코드는 샌드박스 미리보기에서 열고 서버로 보내지 않는다. 데이터 URL 이미지는 코드 안에 넣을 수 있다.</p>
          </aside>
        </div>

        {message && <div className={`workspace-message ${result ? 'success' : ''}`}>{message}</div>}
      </main>

      <footer className="action-dock">
        <div className="dock-status">
          {processing ? <><LoaderCircle className="spin" size={20} /><span><strong>코드 굽는 중</strong><small>브라우저에서 렌더링</small></span></> : result ? <><CheckCircle2 size={21} /><span><strong>끝. 이미지 준비</strong><small>{format.toUpperCase()}</small></span></> : <><CodeXml size={20} /><span><strong>돌릴 준비 끝</strong><small>서버 전송 없음</small></span></>}
        </div>
        {result ? (
          <button type="button" className="primary-button download-button" onClick={() => saveBlob(result.blob, result.name)}><Download size={19} /> 끝난 파일 받기</button>
        ) : (
          <button type="button" className="primary-button" onClick={() => void makeImage()} disabled={processing || !html.trim()}>{processing ? <LoaderCircle className="spin" size={19} /> : <span className="button-arrow">→</span>}{processing ? '코드 굽는 중' : '이미지로 뽑기'}</button>
        )}
      </footer>
    </div>
  )
}
